<script setup lang="ts">
import { onMounted, ref } from 'vue';
import ProgressSpinner from 'primevue/progressspinner';
import Chart from 'primevue/chart';
import Menu from 'primevue/menu';
import { Header, CustomButton, ButtonType, NewGraphDialog } from '@/components';
import { DATE_FORMAT_4, formattedDate } from '@/utils';
import type { Graph } from '@/models';
import { useGraphStore } from '@/stores/graph';
import { useFormStore } from '@/stores/forms';

const graphStore = useGraphStore();

const formStore = useFormStore();

const openCreateGraphDialog = ref(false);

const createNewGraph = (close: boolean = false) => {
  openCreateGraphDialog.value = !openCreateGraphDialog.value;
  if (close) {
    selectedGraph.value = undefined;
  }
};

const getMenuItems = () => {
  return [
    {
      label: 'Delete',
      icon: 'pi pi-trash',
      command: () => {
        deleteGraph();
      }
    }
    // TODO: Add backend support for editing and then enable this
    // {
    //   label: 'Edit',
    //   icon: 'pi pi-pencil',
    //   command: () => {
    //     createNewGraph();
    //   }
    // }
  ];
};

const selectedGraph = ref<Graph>();
const menu = ref();
const items = ref(getMenuItems());

const toggle = (event: PointerEvent, graph: Graph) => {
  menu.value.toggle(event);
  selectedGraph.value = graph;
};

const deleteGraph = () => {
  if (selectedGraph.value) {
    graphStore.deleteGraph(selectedGraph.value!.identifier);
  }
};

onMounted(async () => {
  await formStore.getStudyForms();
  await graphStore.getGraphs();
});
</script>

<template>
  <div class="analytics-root">
    <div class="analytics-wrapper">
      <div class="header">
        <Header title="Analytics">
          <template #headerButtons>
            <CustomButton text="Create graph"
              :type="ButtonType.POSITIVE"
              :icon="{ icon: 'chart-simple', style: 'fal' }"
              @onClick="createNewGraph"/>
          </template>
        </Header>
      </div>

      <template v-if="graphStore.graphs?.length">
        <div class="graphs">
          <div class="graph-item"
            v-for="graph in graphStore.graphs"
            :key="graph.identifier">
            <div class="loader" v-if="graph.loading">
              <ProgressSpinner strokeWidth="4" />
            </div>
            <div class="graph-data" v-if="!graph.loading">
              <div class="graph-header">
                <h3 class="graph-form">{{ graph.formName }}</h3>
                <CustomButton :icon="{ style: 'far', icon: 'ellipsis-vertical' }"
                  :type="ButtonType.ICON_ONLY"
                  @onClick="(e) => toggle(e, graph)"
                  class="option-button"/>
              </div>
              <div class="sub-header">
                <div class="graph-field">{{ graph.fieldName }}</div>
                <div class="graph-created">
                  {{ formattedDate(graph.createdAt, DATE_FORMAT_4) }}
                </div>
              </div>
              <Chart :type="graph.chartType"
                :data="graph.graphData"
                class="chart"/>
            </div>
          </div>
        </div>
      </template>

      <template v-else>
        <!-- <div class="graphs-wrapper">
          <font-awesome-icon :icon="['fal', 'cloud-xmark']" />
          <div class="empty-text">
            No graphs have been created yet. Please use the "Create Graph" button to add a new one.
          </div>
        </div> -->
        <div class="empty-content-container">
          <div class="empty-content">
            <FontAwesomeIcon :icon="[`fal`, 'chart-bar']" class="file-icon" />
            <h3 class="no-entries-label">No graphs have been created yet.</h3>
            <div class="new-entry">
              <div class="new-entry-text">To create a chart click on</div>
              <CustomButton text="Create graph"
              :type="ButtonType.POSITIVE"
              :icon="{ icon: 'chart-simple', style: 'fal' }"
              @onClick="createNewGraph"/>
            </div>
          </div>
        </div>
      </template>

      <Menu ref="menu"
id="overlay_menu"
:model="items"
:popup="true" />
    </div>

    <NewGraphDialog :visible="openCreateGraphDialog"
      @onClose="createNewGraph(true)"
      v-if="openCreateGraphDialog"
      :existing-graph="selectedGraph"/>
  </div>
</template>

<style lang="scss" scoped>
.analytics-root {
  padding: 48px 100px 40px 80px;
  height: 100%;
  width: 100%;
}
.analytics-wrapper {
  min-width: 1100px;
  padding-bottom: 50px;
  display: flex;
  flex-direction: column;
}
.header {
  max-width: 1100px;
}
.graphs {
  display: grid;
  gap: 50px;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  &-wrapper {
    display: flex;
    align-items: center;
    gap: 16px;
    max-width: 100%;
    width: 100%;
    padding: 40px;
    border-width: 1px;
    border-color: #d9dee4;
    border-radius: 4px;
    background-color: #fff;
    box-shadow: 0 0 0 1px #ebeff2, 0 1px 10px 1px rgba(15, 15, 15, 0.05), 0 1px 3px 1px rgba(15, 15, 15, 0.05);
  }
}
.empty-text {
  color: #272a2e;
  line-height: 20px;
  font-size: 14px;
}
.graph-item {
  width: 100%;
  min-height: 500px;
  padding: 20px;
  background-color: white;
  margin-top: 16px;
  border-radius: 4px;
  box-shadow: 0 0 8px 2px rgba(0, 0, 0, 0.15);

  .loader {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
  }

  .chart {
    margin-top: 16px;
  }

  .graph-form {
    font-weight: 600;
  }

  .graph-header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .option-button {
      min-width: 24px;
      margin-right: -12px;
      justify-content: end;
    }
  }

  .sub-header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    .graph-field {
      font-weight: 400;
    }

    .graph-created {
      flex-shrink: 0;
      margin-left: 12px;
    }
  }
}
.empty-content-container {
  padding: 50px 0px;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  box-shadow: 0 0 0 1px #ebeff2, 0 1px 10px 1px rgba(15, 15, 15, 0.05), 0 1px 3px 1px rgba(15, 15, 15, 0.05);
  .empty-content {
    text-align: center;
    .file-icon {
      font-size: 100px;
      margin: 16px 0px;
    }

    .no-entries-label {
      font-weight: bold;
    }

    .new-entry {
      margin: 16px 0px;
      display: flex;
      align-items: center;
      &-text {
        margin-right: 16px;
      }
    }
  }
}

</style>
